import React, { useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Trash2, Edit, CheckCircle } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const PaymentManager = ({ 
  totalAmount = 0,
  payments = [],
  onPaymentsChange,
  accountClosed = false,
  onAccountClosedChange,
  disabled = false,
  bankAccounts = [],
  suppliers = [],
  consolidatedPaidNote = ''
}) => {
  // Track auto-managed rows: date filled once; amount kept synced to agreed total until user edits it
  const initializedDate = useRef(new Set());
  const userEditedAmount = useRef(new Set());
  const followers = useRef(new Set());
  const observed = useRef(new Map());
  const remember = (row) => observed.current.set(row.id, `${row.amount || ''}|${row.method || ''}`);

  const getToday = () => {
    const d = new Date();
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return local.toISOString().split('T')[0];
  };

  // Auto-fill today's date + keep agreed amount synced on blank (auto-managed) payment rows
  useEffect(() => {
    if (disabled) return;
    let changed = false;
    const updated = payments.map((p) => {
      let np = p;
      const others = payments.reduce((sum, x) => x.id === p.id ? sum : sum + (parseFloat(x.amount) || 0), 0);
      // Re-evaluate a row whenever it changed from outside (new row / record loaded): it follows the total only if blank or holding the full balance
      if (observed.current.get(p.id) !== `${p.amount || ''}|${p.method || ''}`) {
        const follows = !p.method || Math.abs((parseFloat(p.amount) || 0) - (totalAmount - others)) < 0.01;
        if (follows) followers.current.add(p.id); else followers.current.delete(p.id);
      }
      const autoManaged = !userEditedAmount.current.has(p.id) && followers.current.has(p.id);
      if (autoManaged && !p.paymentDate && !initializedDate.current.has(p.id)) {
        np = { ...np, paymentDate: getToday() };
        initializedDate.current.add(p.id);
        changed = true;
      }
      if (autoManaged) {
        const target = totalAmount - others;
        if (target > 0) {
          const targetStr = target.toFixed(2);
          if ((np.amount || '') !== targetStr) {
            np = { ...np, amount: targetStr };
            changed = true;
          }
        }
      }
      remember(np);
      return np;
    });
    if (changed) onPaymentsChange(updated);
  }, [payments, totalAmount, disabled]);

  // Payment management functions
  const handlePaymentChange = (id, field, value) => {
    if (field === 'amount') {
      userEditedAmount.current.add(id);
    }
    const updatedPayments = payments.map(payment =>
      payment.id === id ? { ...payment, [field]: value } : payment
    );
    remember(updatedPayments.find(p => p.id === id));
    onPaymentsChange(updatedPayments);

    // Auto-add check row when check date is filled and amount doesn't cover total
    if (field === 'checkDate' && value) {
      const changedPayment = updatedPayments.find(p => p.id === id);
      if (changedPayment && changedPayment.method === 'check' && changedPayment.amount) {
        autoAddCheckRow(updatedPayments, changedPayment);
      }
    }
  };

  const autoAddCheckRow = (currentPayments, sourceCheck) => {
    const totalPaid = currentPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    const remaining = totalAmount - totalPaid;

    if (remaining > 0 && sourceCheck.amount) {
      // Calculate next check number
      const checkNum = parseInt(sourceCheck.checkNumber) || 0;
      const nextCheckNum = String(checkNum + 1);

      // Calculate next month date
      let nextDate = '';
      if (sourceCheck.checkDate) {
        const d = new Date(sourceCheck.checkDate);
        d.setMonth(d.getMonth() + 1);
        nextDate = d.toISOString().split('T')[0];
      }

      const newId = String(Math.max(...currentPayments.map(p => parseInt(p.id) || 0)) + 1);

      // Only auto-add if there isn't already a pending empty check row
      const hasEmptyCheck = currentPayments.some(p => p.method === 'check' && !p.amount && p.id !== sourceCheck.id);
      if (!hasEmptyCheck) {
        onPaymentsChange([...currentPayments, {
          id: newId,
          method: 'check',
          amount: sourceCheck.amount,
          notes: '',
          depositedTo: sourceCheck.depositedTo || '',
          checkNumber: nextCheckNum,
          checkDate: nextDate,
          bankName: sourceCheck.bankName || '',
          branch: sourceCheck.branch || '',
          accountNumber: sourceCheck.accountNumber || ''
        }]);
      }
    }
  };

  const addPayment = () => {
    const newId = payments.length > 0 
      ? String(Math.max(...payments.map(p => parseInt(p.id) || 0)) + 1) 
      : '1';
    
    onPaymentsChange([...payments, {
      id: newId,
      method: '',
      amount: '',
      notes: '',
      depositedTo: '',
      checkNumber: '',
      checkDate: '',
      bankName: '',
      branch: '',
      accountNumber: ''
    }]);
  };

  const removePayment = (id) => {
    if (payments.length > 1) {
      onPaymentsChange(payments.filter(payment => payment.id !== id));
    } else {
      toast({
        title: 'שגיאה',
        description: 'חייב להיות לפחות תשלום אחד',
        variant: 'destructive'
      });
    }
  };

  // Calculate totals
  const calculateTotalPaid = () => {
    // Only count payments that have a method selected — a method-less amount is
    // dropped on save, so it must not make the balance look settled.
    return payments.reduce((sum, payment) => sum + (payment.method ? (parseFloat(payment.amount) || 0) : 0), 0);
  };

  const calculateBalance = () => {
    return totalAmount - calculateTotalPaid();
  };

  // Track balance for display purposes only
  const balance = calculateBalance();

  return (
    <div className="space-y-4 mt-6 p-6 bg-amber-50 rounded-lg border-2 border-amber-200">
      <div className="flex justify-between items-center">
        <h3 className="text-2xl font-bold text-amber-900">פרטי תשלום</h3>
        <div className="flex gap-3">
          <Button
            type="button"
            onClick={addPayment}
            disabled={disabled}
            className={`${disabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-amber-600 hover:bg-amber-700'}`}
            title={disabled ? 'לא ניתן להוסיף תשלום' : 'הוסף תשלום'}
          >
            הוסף תשלום
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className={`${accountClosed || disabled ? 'opacity-50 cursor-not-allowed' : 'border-blue-500 text-blue-600 hover:bg-blue-50'}`}
            title={disabled ? 'לא ניתן לערוך' : 'ערוך פרטי תשלום'}
          >
            <Edit className="ml-2 w-4 h-4" />
            עריכה
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {payments.map((payment) => (
          <Card key={payment.id} className="bg-white">
            <CardContent className="p-4">
              <div className="grid grid-cols-12 gap-3 items-end">
                {/* Payment Method */}
                <div className="col-span-2">
                  <Label>אמצעי תשלום</Label>
                  <select
                    value={payment.method}
                    onChange={(e) => handlePaymentChange(payment.id, 'method', e.target.value)}
                    disabled={disabled}
                    className="w-full p-2 border rounded"
                  >
                    <option value="">בחר...</option>
                    <option value="cash">מזומן</option>
                    <option value="credit">כרטיס אשראי</option>
                    <option value="check">שיק</option>
                    <option value="transfer">העברה בנקאית</option>
                  </select>
                </div>

                {/* Amount */}
                <div className="col-span-2">
                  <Label>סכום</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={payment.amount}
                    onChange={(e) => handlePaymentChange(payment.id, 'amount', e.target.value)}
                    placeholder="0"
                    disabled={disabled}
                  />
                  {!disabled && (
                    <button type="button" onClick={() => handlePaymentChange(payment.id, 'amount', '0')} className="mt-1 text-xs font-semibold text-rose-600 hover:text-rose-800 hover:underline" data-testid={`payment-no-payment-btn-${payment.id}`}>
                      ללא תשלום
                    </button>
                  )}
                </div>

                {/* Payment Date */}
                <div className="col-span-2">
                  <Label>תאריך תשלום</Label>
                  <Input
                    type="date"
                    value={payment.paymentDate || ''}
                    onChange={(e) => handlePaymentChange(payment.id, 'paymentDate', e.target.value)}
                    disabled={disabled}
                    data-testid={`payment-date-${payment.id}`}
                  />
                </div>

                {/* Has Invoice */}
                <div className="col-span-1">
                  <Label>חשבונית</Label>
                  <button
                    type="button"
                    onClick={() => handlePaymentChange(payment.id, 'hasReceipt', !payment.hasReceipt)}
                    disabled={disabled}
                    className={`w-full h-10 rounded border-2 flex items-center justify-center transition-all ${
                      payment.hasReceipt
                        ? 'bg-green-500 border-green-500 text-white'
                        : 'border-gray-300 hover:border-green-400 text-transparent hover:text-green-300'
                    }`}
                    title={payment.hasReceipt ? 'יש חשבונית' : 'אין חשבונית'}
                    data-testid={`payment-receipt-${payment.id}`}
                  >
                    <CheckCircle className="w-5 h-5" />
                  </button>
                </div>

                {/* Check-specific fields - order: Bank Name, Branch, Account Number, Check Number, Check Date */}
                {payment.method === 'check' && (
                  <>
                    <div className="col-span-2">
                      <Label>שם בנק</Label>
                      <Input
                        value={payment.bankName}
                        onChange={(e) => handlePaymentChange(payment.id, 'bankName', e.target.value)}
                        disabled={disabled}
                      />
                    </div>
                    <div className="col-span-1">
                      <Label>סניף</Label>
                      <Input
                        value={payment.branch}
                        onChange={(e) => handlePaymentChange(payment.id, 'branch', e.target.value)}
                        disabled={disabled}
                        data-testid={`payment-branch-${payment.id}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>מס&apos; חשבון</Label>
                      <Input
                        value={payment.accountNumber}
                        onChange={(e) => handlePaymentChange(payment.id, 'accountNumber', e.target.value)}
                        disabled={disabled}
                      />
                    </div>
                    <div className="col-span-1">
                      <Label>מס&apos; שיק</Label>
                      <Input
                        value={payment.checkNumber}
                        onChange={(e) => handlePaymentChange(payment.id, 'checkNumber', e.target.value)}
                        disabled={disabled}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>תאריך שיק <span className="text-red-500">*</span></Label>
                      <Input
                        type="date"
                        value={payment.checkDate}
                        onChange={(e) => handlePaymentChange(payment.id, 'checkDate', e.target.value)}
                        disabled={disabled}
                        className={payment.amount && !payment.checkDate ? 'border-red-400 focus:border-red-500' : ''}
                        data-testid={`payment-check-date-${payment.id}`}
                      />
                      {payment.amount && !payment.checkDate && (
                        <p className="text-xs text-red-500 mt-1">חובה למלא תאריך פירעון</p>
                      )}
                    </div>
                  </>
                )}

                {/* Transfer-specific fields */}
                {payment.method === 'transfer' && (
                  <>
                    <div className="col-span-6">
                      <Label>בחר חשבון בנק</Label>
                      <select
                        value={payment.accountNumber}
                        onChange={(e) => {
                          const selectedAccount = bankAccounts.find(acc => acc.accountNumber === e.target.value);
                          if (selectedAccount) {
                            handlePaymentChange(payment.id, 'bankName', selectedAccount.bankName);
                            handlePaymentChange(payment.id, 'branch', selectedAccount.branch);
                            handlePaymentChange(payment.id, 'accountNumber', selectedAccount.accountNumber);
                          }
                        }}
                        disabled={disabled}
                        className="w-full p-2 border rounded"
                      >
                        <option value="">בחר חשבון בנק...</option>
                        {bankAccounts.map(acc => (
                          <option key={acc.id} value={acc.accountNumber}>
                            {acc.bankName} - סניף {acc.branch} - חשבון {acc.accountNumber}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {/* Notes */}
                <div className={payment.method === 'check' || payment.method === 'transfer' ? 'col-span-2' : 'col-span-3'}>
                  <Label>הערות</Label>
                  <Input
                    value={payment.notes}
                    onChange={(e) => handlePaymentChange(payment.id, 'notes', e.target.value)}
                    placeholder="הערות..."
                    disabled={disabled}
                  />
                </div>

                {/* Deposited To */}
                <div className="col-span-2">
                  <Label>התשלום הופקד ל</Label>
                  <select
                    value={payment.depositedTo || ''}
                    onChange={(e) => handlePaymentChange(payment.id, 'depositedTo', e.target.value)}
                    disabled={disabled}
                    className="w-full p-2 border rounded text-sm"
                    data-testid={`payment-deposited-to-${payment.id}`}
                  >
                    <option value="">בחר חשבון...</option>
                    {bankAccounts.map((acc) => (
                      <option key={acc.id || acc.bankName} value={`${acc.bankName} - ${acc.branch} - ${acc.accountNumber}`}>
                        {acc.bankName} - סניף {acc.branch} - חשבון {acc.accountNumber}
                      </option>
                    ))}
                    {suppliers.map((s) => (
                      <option key={s.id} value={s.name}>{s.name}</option>
                    ))}
                    {/* Keep saved value visible even if not in current lists */}
                    {payment.depositedTo && 
                      !bankAccounts.some(a => `${a.bankName} - ${a.branch} - ${a.accountNumber}` === payment.depositedTo) &&
                      !suppliers.some(s => s.name === payment.depositedTo) && (
                      <option value={payment.depositedTo}>{payment.depositedTo}</option>
                    )}
                  </select>
                </div>

                {/* Delete button */}
                <div className="col-span-1">
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => removePayment(payment.id)}
                    disabled={payments.length === 1 || accountClosed || disabled}
                    title={disabled ? 'לא ניתן למחוק' : payments.length === 1 ? 'חייב להיות לפחות תשלום אחד' : 'מחק תשלום'}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Payment Summary */}
      {consolidatedPaidNote ? (
        <div className="mt-6 bg-green-50 border-2 border-green-300 rounded-lg p-5 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            </div>
            <p className="text-lg font-bold text-green-800">{consolidatedPaidNote}</p>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-3">
            <div>
              <p className="text-xs text-gray-500">סכום מקורי</p>
              <p className="text-lg font-bold text-gray-700">₪{totalAmount.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">שולם דרך חשבון מרוכז</p>
              <p className="text-lg font-bold text-green-700">₪{totalAmount.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">יתרה</p>
              <p className="text-lg font-bold text-green-700">₪0.00</p>
            </div>
          </div>
        </div>
      ) : (
      <div className="grid grid-cols-3 gap-4 mt-6">
        <Card className="bg-blue-100 border-blue-300">
          <CardContent className="p-4 text-center">
            <p className="text-sm text-blue-800">סה&quot;כ סוכם עם הלקוח</p>
            <p className="text-2xl font-bold text-blue-900">₪{totalAmount.toFixed(2)}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-green-100 border-green-300">
          <CardContent className="p-4 text-center">
            <p className="text-sm text-green-800">סה&quot;כ שולם</p>
            <p className="text-2xl font-bold text-green-900">₪{calculateTotalPaid().toFixed(2)}</p>
          </CardContent>
        </Card>
        
        <Card className={calculateBalance() > 0 ? "bg-red-100 border-red-300" : calculateBalance() < 0 ? "bg-yellow-100 border-yellow-300" : "bg-gray-100 border-gray-300"}>
          <CardContent className="p-4 text-center">
            <p className="text-sm">{calculateBalance() > 0 ? "יתרה לתשלום" : calculateBalance() < 0 ? "עודף לתשלום" : "יתרה"}</p>
            <p className={`text-2xl font-bold ${calculateBalance() > 0 ? 'text-red-900' : calculateBalance() < 0 ? 'text-yellow-900' : 'text-gray-900'}`}>
              ₪{Math.abs(calculateBalance()).toFixed(2)}
            </p>
          </CardContent>
        </Card>
      </div>
      )}
    </div>
  );
};

export default PaymentManager;
